import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SearchableSelectProps {
  options: SelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  /** Global rule 3: dropdowns with MORE than this many options get a search box. */
  searchThreshold?: number;
  disabled?: boolean;
  id?: string;
  /**
   * Switches the widget into server-side (async) mode: the caller — not
   * this component — owns the option set. Fires on every keystroke in the
   * search box with the raw typed text, and again with `''` whenever the
   * popup closes (selection, Escape, outside click), mirroring the
   * `common/SearchableSelect` contract so callers migrate mechanically.
   *
   * Providing this prop is the *only* switch for server-side mode — there
   * is deliberately no separate `serverSide`/`filterMode` flag to keep in
   * sync with it. Two independent props ("I pass onQueryChange" +
   * "I remember to also flip serverSide") is exactly the kind of thing
   * that's easy to forget and silently double-filter server results (the
   * bug this API exists to prevent); one prop can't be half-set.
   *
   * Server-side mode changes two things automatically:
   *  - the local case-insensitive substring filter is skipped entirely —
   *    `options` is trusted as already reflecting the current query, so
   *    server results are never re-filtered (and possibly hidden) client-side.
   *  - the search box always renders regardless of `options.length` /
   *    `searchThreshold`, since a short current page of server results
   *    (e.g. 0-5 while the user is still typing) must not hide the only
   *    way to keep searching.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Shows an accessible pending affordance while the caller fetches options
   * for the current query. Purely presentational — doesn't affect what's
   * rendered in the option list, so previously-loaded options (e.g. from
   * the prior keystroke) stay visible and selectable while a new page loads.
   */
  loading?: boolean;
}

/**
 * Accessible single-select dropdown for global rule 3 ("dropdowns with more
 * than five options must be searchable"). A search box only renders when
 * `options.length > searchThreshold` (default 5); five or fewer options get
 * a plain listbox trigger. Search is case-insensitive substring matching on
 * the option label.
 *
 * Implements the WAI-ARIA combobox pattern: `role="combobox"` on the
 * trigger (a button when there's no search box, since it owns the popup
 * directly), `role="listbox"` / `role="option"` on the popup, and full
 * keyboard support — ArrowUp/ArrowDown move the highlighted option
 * (wrapping at the ends), Enter selects it, Escape closes and returns focus
 * to the trigger, and Tab exits the widget (closing it) without selecting.
 * Opening focuses the search input when one is present.
 *
 * Also supports server-side/async option sets via `onQueryChange` (see its
 * doc comment): pass it and the component always shows a search box,
 * reports keystrokes instead of filtering `options` itself, and — paired
 * with `loading` — renders an accessible pending state while a fetch is in
 * flight.
 */
export function SearchableSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  searchThreshold = 5,
  disabled = false,
  id,
  onQueryChange,
  loading = false,
}: SearchableSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const listboxId = useId();
  const autoId = useId();
  const triggerId = id ?? autoId;

  // Server-side mode: the caller drives the option set via onQueryChange,
  // so the search box must always be offered (a short current page must
  // not hide the only way to keep searching) and the local filter must
  // stay out of the way of results the caller already filtered.
  const serverDriven = onQueryChange != null;
  const searchable = serverDriven || options.length > searchThreshold;
  const selected = options.find((o) => o.value === value) ?? null;

  const filtered = useMemo(() => {
    if (serverDriven) return options;
    if (!searchable || !query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query, searchable, serverDriven]);

  const optionId = (index: number) => `${listboxId}-option-${index}`;
  const activeDescendant =
    open && activeIndex >= 0 && filtered[activeIndex] ? optionId(activeIndex) : undefined;

  // Whenever the popup opens (or the filtered list changes while open), make
  // sure something sensible is highlighted: the current value if it's still
  // visible, otherwise the first match.
  useEffect(() => {
    if (!open) return;
    if (filtered.length === 0) {
      setActiveIndex(-1);
      return;
    }
    const selectedIndex = filtered.findIndex((o) => o.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered]);

  // Opening focuses the search input when present.
  useEffect(() => {
    if (open && searchable) {
      searchRef.current?.focus();
    }
  }, [open, searchable]);

  // Closing (for any reason focus leaves the widget) resets search state.
  function close() {
    setOpen(false);
    setQuery('');
    onQueryChange?.('');
  }

  function closeAndFocusTrigger() {
    close();
    triggerRef.current?.focus();
  }

  function openDropdown() {
    if (disabled) return;
    setOpen(true);
  }

  function selectOption(option: SelectOption) {
    onChange(option.value);
    closeAndFocusTrigger();
  }

  function moveActive(delta: 1 | -1) {
    if (filtered.length === 0) return;
    setActiveIndex((prev) => {
      const base = prev < 0 ? (delta > 0 ? -1 : 0) : prev;
      return (base + delta + filtered.length) % filtered.length;
    });
  }

  // Shared Arrow/Enter/Escape handling for whichever element currently owns
  // focus while the popup is open (the trigger button when there's no
  // search box, or the search input when there is one).
  function handleOpenKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        moveActive(1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        moveActive(-1);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeIndex >= 0 && filtered[activeIndex]) selectOption(filtered[activeIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        closeAndFocusTrigger();
        break;
      default:
        break;
    }
  }

  function handleTriggerKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        openDropdown();
      }
      return;
    }
    // Once open with a search box, focus moves to the input and this
    // handler stops receiving key events for the widget; when there's no
    // search box the trigger stays focused and owns navigation directly.
    handleOpenKeyDown(e);
  }

  // Tab (or any focus-out) closes the popup without selecting, letting
  // focus continue to wherever the browser sends it natively.
  function handleBlur(e: React.FocusEvent<HTMLDivElement>) {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      close();
    }
  }

  return (
    <div ref={containerRef} className="relative w-full" onBlur={handleBlur}>
      <button
        ref={triggerRef}
        type="button"
        id={triggerId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listboxId}
        aria-activedescendant={searchable ? undefined : activeDescendant}
        disabled={disabled}
        onClick={() => (open ? close() : openDropdown())}
        onKeyDown={handleTriggerKeyDown}
        className={cn(
          'flex w-full items-center justify-between rounded-lg border py-2 pl-3 pr-1.5 text-left transition-colors duration-200',
          'focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400',
          'cursor-pointer bg-white disabled:cursor-not-allowed disabled:bg-gray-50 dark:bg-gray-700 dark:disabled:bg-gray-800',
          'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
        )}
      >
        <span className={cn('truncate', selected ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400')}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronUpDownIcon className="h-5 w-5 flex-shrink-0 text-gray-400" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 max-h-72 w-full overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {searchable && (
            <div className="border-b border-gray-100 p-2 dark:border-gray-700">
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  onQueryChange?.(e.target.value);
                }}
                onKeyDown={handleOpenKeyDown}
                placeholder="Search..."
                aria-controls={listboxId}
                aria-activedescendant={activeDescendant}
                autoComplete="off"
                className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:focus:ring-primary-400"
              />
            </div>
          )}
          {loading && (
            <div
              role="status"
              aria-live="polite"
              className="border-b border-gray-100 px-3 py-2 text-sm text-gray-400 dark:border-gray-700"
            >
              Loading...
            </div>
          )}
          <ul id={listboxId} role="listbox" className="max-h-56 overflow-y-auto py-1">
            {!loading && filtered.length === 0 && (
              <li className="px-3 py-2 text-sm text-gray-400">No matches found</li>
            )}
            {filtered.map((o, index) => (
              <li
                key={o.value}
                id={optionId(index)}
                role="option"
                aria-selected={o.value === value}
                onMouseEnter={() => setActiveIndex(index)}
                // Options aren't focusable (activedescendant owns "virtual"
                // focus instead), so a plain click would first blur the
                // search input/trigger, which our blur handler treats as
                // "focus left the widget" and closes the popup before the
                // click can land. Suppress that focus change on mousedown.
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectOption(o)}
                className={cn(
                  'flex cursor-pointer items-center justify-between px-3 py-2 text-sm',
                  index === activeIndex
                    ? 'bg-primary-50 dark:bg-primary-900/30'
                    : 'hover:bg-primary-50 dark:hover:bg-primary-900/30',
                  o.value === value
                    ? 'font-medium text-primary-700 dark:text-primary-400'
                    : 'text-gray-700 dark:text-gray-300'
                )}
              >
                <span className="truncate">{o.label}</span>
                {o.value === value && <CheckIcon className="h-4 w-4 flex-shrink-0" />}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
